import React, { useState, useEffect } from 'react';
import { useClientPreset } from '../contexts/ClientPresetContext';
import { CampaignConfig, TargetingOptions } from '../types/client';
import { campaignTypes, campaignCategories, bidStrategies, countries } from '../data/campaignTypes';
import { assetsApi } from '../lib/api';
import { campaignsApi } from '../lib/api';
// import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
// import { FaImage, FaVideo, FaPlay, FaEye, FaBrain, FaUsers, FaGlobe, FaClipboard, FaExternalLinkAlt, FaChevronLeft } from 'react-icons/fa';
import VideoAsset from '../components/VideoAsset';
//
interface Step {
  id: number;
  title: string;
  description: string;
}

interface AdVariant {
  id: number;
  headline: string;
  body: string;
  ad_name?: string;  // Custom ad name for this variant
  asset_id?: number;
  asset_url: string;
  asset_thumbnail_url?: string;
  asset_type: string;
  preview_url?: string;
  is_approved: boolean;
  meta_creative_id?: string;
  created_at: string;
  original_title?: string;
  original_description?: string;
}

const steps: Step[] = [
  { id: 1, title: 'Client & Campaign Type', description: 'Select client and campaign objective' },
  { id: 2, title: 'Budget & Schedule', description: 'Set budget and timing' },
  { id: 3, title: 'Targeting', description: 'Define your audience' },
  { id: 4, title: 'Conversion Events', description: 'Select tracking events' },
  { id: 5, title: 'Ad Variations', description: 'Select ads and upload assets' },
  { id: 6, title: 'Review & Launch', description: 'Review and create campaign' }
];

const AdvancedCampaignPage: React.FC = () => {
  // const navigate = useNavigate()
  const { presets, selectedPreset, selectPreset } = useClientPreset();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [isMultiCampaignMode, setIsMultiCampaignMode] = useState(false);
  const [roasTarget, setRoasTarget] = useState(1.6); // Default ROAS target

  // Existing campaign mode
  const [isExistingCampaignMode, setIsExistingCampaignMode] = useState(false);
  const [existingMetaCampaigns, setExistingMetaCampaigns] = useState<Array<{
    id: string; name: string; status: string; effective_status: string; objective: string;
  }>>([]);
  const [selectedExistingCampaignId, setSelectedExistingCampaignId] = useState('');
  const [loadingExistingCampaigns, setLoadingExistingCampaigns] = useState(false);
  
  // Campaign creation result state
  const [campaignResult, setCampaignResult] = useState<{
    success: boolean;
    message: string;
    campaignId?: string;
    adsCreated?: number;
    campaignsCreated?: number;
    totalAdsCreated?: number;
  } | null>(null);
  
  // Conversion events available for selection
  const [conversionEvents] = useState([
    { id: 'PURCHASE', name: 'Purchase', description: 'Someone bought a product' },
    { id: 'ADD_TO_CART', name: 'Add to Cart', description: 'Someone added item to cart' },
    { id: 'VIEW_CONTENT', name: 'View Content', description: 'Someone viewed a page' },
    { id: 'LEAD', name: 'Lead', description: 'Someone submitted contact info' },
    { id: 'COMPLETE_REGISTRATION', name: 'Complete Registration', description: 'Someone signed up' },
    { id: 'INITIATE_CHECKOUT', name: 'Initiate Checkout', description: 'Someone started checkout' },
    { id: 'ADD_PAYMENT_INFO', name: 'Add Payment Info', description: 'Someone added payment method' },
    { id: 'SEARCH', name: 'Search', description: 'Someone searched on your site' },
    { id: 'ADD_TO_WISHLIST', name: 'Add to Wishlist', description: 'Someone saved item' },
    { id: 'CONTACT', name: 'Contact', description: 'Someone contacted you' },
    { id: 'SUBSCRIBE', name: 'Subscribe', description: 'Someone subscribed' },
    // Underoutfit custom events
    { id: 'Marti_PageView', name: 'Marti PageView', description: 'Underoutfit page view event' },
    { id: 'Marti_Purchase', name: 'Marti Purchase', description: 'Underoutfit purchase event' },
    { id: 'Marti-Add_To_Cart', name: 'Marti Add To Cart', description: 'Underoutfit add to cart event' }
  ]);

  const [campaign, setCampaign] = useState<Partial<CampaignConfig>>({
    name: '',
    campaignType: '',
    objective: '',
    optimizationGoal: '',
    budgetType: 'lifetime',
    budget: 0,
    startDate: new Date().toISOString().split('T')[0],
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
    specialAdCategories: [],
    advantageAudience: true,
    targeting: {
      countries: [],
      ageMin: 18,
      ageMax: 65,
      genders: []
    },
    creative: {
      type: 'image',
      headline: '',
      primaryText: '',
      description: '',
      callToAction: 'SHOP_NOW',
      websiteUrl: ''
    },
    conversionTracking: {
      pixelId: '',
      conversionEvents: []
    }
  });

  // Multi-campaign specific state
  const [multiCampaignConfig, setMultiCampaignConfig] = useState({
    baseName: '',
    numCampaigns: 2,
    budgetPerCampaign: 0,
    distributionMethod: 'round_robin' as 'round_robin' | 'sequential',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    duration: 7 as number | null
  });

  const [selectedCategory, setSelectedCategory] = useState('');
  const [variants, setVariants] = useState<AdVariant[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const handleTargetingChange = (field: keyof TargetingOptions, value: any) => {
    setCampaign(prev => ({
      ...prev,
      targeting: {
        ...prev.targeting!,
        [field]: value
      }
    }));
  };

  const handleConversionEventToggle = (eventId: string) => {
    setCampaign(prev => {
      const currentEvents = prev.conversionTracking?.conversionEvents || [];
      const newEvents = currentEvents.includes(eventId)
        ? currentEvents.filter(e => e !== eventId)
        : [...currentEvents, eventId];
      
      return {
        ...prev,
        conversionTracking: {
          ...prev.conversionTracking!,
          conversionEvents: newEvents
        }
      };
    });
  };

  // Initialize with selected preset defaults
  useEffect(() => {
    if (selectedPreset) {
      setCampaign(prev => ({
        ...prev,
        budget: selectedPreset.defaultBudget,
        targeting: {
          ...prev.targeting!,
          countries: selectedPreset.defaultCountries,
          ageMin: selectedPreset.defaultAgeMin,
          ageMax: selectedPreset.defaultAgeMax,
          genders: selectedPreset.defaultGenders
        },
        creative: {
          ...prev.creative!,
          websiteUrl: selectedPreset.website
        },
        conversionTracking: {
          pixelId: selectedPreset.pixelId,
          conversionEvents: []
        }
      }));
      
      // Also initialize multi-campaign config with preset defaults
      setMultiCampaignConfig(prev => ({
        ...prev,
        baseName: `${selectedPreset.brand}_Campaign`,
        budgetPerCampaign: selectedPreset.defaultBudget,
        duration: selectedPreset.defaultDuration
      }));
    }
  }, [selectedPreset]);

  const fetchExistingMetaCampaigns = async () => {
    if (!selectedPreset) return;
    setLoadingExistingCampaigns(true);
    try {
      const clientId = getClientId();
      const response = await campaignsApi.getMetaCampaigns(clientId);
      setExistingMetaCampaigns(response.data.campaigns || []);
    } catch (err: any) {
      toast.error('Failed to load campaigns from Meta');
      console.error('fetchExistingMetaCampaigns error:', err);
    } finally {
      setLoadingExistingCampaigns(false);
    }
  };

  const handleCampaignTypeSelect = (type: any) => {
    setCampaign(prev => ({
      ...prev,
      campaignType: type.id,
      objective: type.objective,
      optimizationGoal: type.optimizationGoal,
      budget: selectedPreset?.defaultBudget || type.recommendedBudget.suggested,
      name: `${selectedPreset?.brand || 'Campaign'}_${type.name.replace(/\s+/g, '_')}`
    }));
  };

  const handleCountryToggle = (countryCode: string) => {
    const currentCountries = campaign.targeting?.countries || [];
    if (currentCountries.includes(countryCode)) {
      handleTargetingChange('countries', currentCountries.filter(c => c !== countryCode));
    } else {
      handleTargetingChange('countries', [...currentCountries, countryCode]);
    }
  };


  const handleAddToExistingCampaign = async () => {
    if (!selectedPreset || !selectedExistingCampaignId) {
      alert('Please select a client and an existing campaign');
      return;
    }
    if (selectedVariants.length === 0) {
      alert('Please select at least one ad variant');
      return;
    }

    setIsCreating(true);
    try {
      let endDate: string | null = null;
      if (campaign.duration !== null && campaign.duration !== undefined && campaign.duration > 0) {
        const start = new Date(campaign.startDate!);
        const end = new Date(start);
        end.setDate(start.getDate() + campaign.duration);
        endDate = end.toISOString().split('T')[0];
      }

      const clientId = getClientId();
      const selectedConversionEvent = (campaign.conversionTracking?.conversionEvents?.length ?? 0) > 0
        ? campaign.conversionTracking?.conversionEvents?.[0]
        : undefined;

      const selectedCampaignName = existingMetaCampaigns.find(c => c.id === selectedExistingCampaignId)?.name || selectedExistingCampaignId;

      const payload = {
        meta_campaign_id: selectedExistingCampaignId,
        name: campaign.name || `${selectedPreset.brand}_AdSet`,
        budget: (campaign.budget || selectedPreset.defaultBudget) * 100,
        budgetType: campaign.budgetType || 'lifetime',
        start: campaign.startDate!,
        end: endDate,
        variant_ids: selectedVariants,
        client_id: clientId,
        campaign_type: campaign.campaignType,
        objective: campaign.objective,
        optimization_goal: campaign.optimizationGoal,
        ...(campaign.campaignType === 'roas' && { roas_target: roasTarget }),
        ...(selectedConversionEvent && { conversion_event: selectedConversionEvent }),
        ...(campaign.bid_control && { bid_control: campaign.bid_control }),
        utmTemplate: selectedPreset.utmTemplate,
        instagram_account_id: selectedPreset.instagramAccountId,
        landing_page_url: campaign.landingPageUrl,
        ...(campaign.messengerDestination === 'page' && {
          messengerDestination: campaign.messengerDestination,
          messengerCallToAction: campaign.messengerCallToAction,
          messengerGreeting: campaign.messengerGreeting,
          messengerQuickReplies: campaign.messengerQuickReplies
        })
      };

      const response = await campaignsApi.addToExisting(payload);
      if (response.data) {
        setCampaignResult({
          success: true,
          message: `🎉 Added to existing campaign successfully!\n\n📋 Campaign: ${selectedCampaignName}\n📢 Ads Created: ${response.data.ads_created}\n✅ ${response.data.message}\n\nYour new ads have been created in PAUSED status.`,
          campaignId: String(response.data.campaign_id),
          adsCreated: response.data.ads_created
        });
        setSelectedVariants([]);
        setCurrentStep(1);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setCampaignResult({
        success: false,
        message: `Error adding to existing campaign: ${errorMessage}`
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!selectedPreset) {
      alert('Please select a client preset first');
      return;
    }

    // Check if this is a specialized Messenger campaign (which doesn't require variants)
    const isSpecializedMessengerCampaign = campaign.campaignType === 'click_to_messenger';
    
    // Check if this is a regular campaign with Messenger destination
    const hasMessengerDestination = campaign.messengerDestination === 'page';

    if (!isSpecializedMessengerCampaign && selectedVariants.length === 0) {
      alert('Please select at least one ad variant');
      return;
    }

    setIsCreating(true);
    
    try {
      console.log('Creating campaign:', isSpecializedMessengerCampaign ? 'Specialized Messenger' : hasMessengerDestination ? 'Regular with Messenger destination' : 'Regular', 'with variants:', selectedVariants);

      // Calculate end date - null for unlimited campaigns
      let endDate = null;
      if (campaign.duration !== null && campaign.duration !== undefined && campaign.duration > 0) {
        const start = new Date(campaign.startDate!);
        const end = new Date(start);
        end.setDate(start.getDate() + campaign.duration);
        endDate = end.toISOString().split('T')[0];
      } else if (campaign.duration === undefined && selectedPreset.defaultDuration) {
        // Fallback to preset default if duration is undefined (not explicitly set to unlimited)
        const start = new Date(campaign.startDate!);
        const end = new Date(start);
        end.setDate(start.getDate() + selectedPreset.defaultDuration);
        endDate = end.toISOString().split('T')[0];
      }

      const clientId = getClientId();

      // Extract first selected conversion event for regular campaign endpoint
      const selectedConversionEvent = (campaign.conversionTracking?.conversionEvents?.length ?? 0) > 0 
        ? campaign.conversionTracking?.conversionEvents?.[0] 
        : null;

      // Use the advanced campaign API for specialized Messenger campaigns or new campaign flow
      if (isSpecializedMessengerCampaign || selectedVariants.length === 0) {
        const advancedCampaignData = {
          name: campaign.name || `${selectedPreset.brand}_Campaign`,
          campaignType: campaign.campaignType,
          objective: campaign.objective,
          optimizationGoal: campaign.optimizationGoal,
          budgetType: campaign.budgetType,
          budget: (campaign.budget || selectedPreset.defaultBudget),
          startDate: campaign.startDate!,
          endDate: endDate,
          bidStrategy: campaign.bidStrategy,
          bidAmount: campaign.bidAmount,
          bid_control: campaign.bid_control,
          targeting: campaign.targeting,
          creative: campaign.creative,
          conversionTracking: campaign.conversionTracking,
          specialAdCategories: campaign.specialAdCategories,
          advantageAudience: campaign.advantageAudience,
          metaAccountId: selectedPreset.metaAccountId,
          pageId: selectedPreset.pageId,
          pixelId: selectedPreset.pixelId,
          utmTemplate: selectedPreset.utmTemplate,
          instagramAccountId: selectedPreset.instagramAccountId,
          landingPageUrl: campaign.landingPageUrl,
          // Messenger-specific fields (for both specialized Messenger campaigns and regular campaigns with Messenger destination)
          ...((isSpecializedMessengerCampaign || hasMessengerDestination) && {
            messengerDestination: campaign.messengerDestination,
            messengerGreeting: campaign.messengerGreeting,
            messengerCallToAction: campaign.messengerCallToAction,
            messengerTemplate: campaign.messengerTemplate,
            messengerQuickReplies: campaign.messengerQuickReplies
          }),
          // Include selected assets for Click-to-Messenger campaigns with assets
          ...(selectedVariants.length > 0 && {
            selectedAssets: selectedVariants.map(variantId => {
              const variant = variants.find(v => v.id === variantId);
              return variant ? {
                id: variant.asset_id || variant.id,
                fbid: variant.asset_id || variant.id, // Use asset_id as fbid
                type: variant.asset_type,
                url: variant.asset_url,
                thumbnail_url: variant.asset_thumbnail_url
              } : null;
            }).filter(Boolean) // Remove any null values
          })
        };

        console.log('Creating advanced campaign with data:', advancedCampaignData);
        const response = await campaignsApi.createAdvanced(advancedCampaignData);
        
        if (response.data) {
          const successMessage = `🎉 ${isSpecializedMessengerCampaign ? 'Messenger' : hasMessengerDestination ? 'Campaign with Messenger destination' : 'Advanced'} campaign created successfully!
          
Campaign ID: ${response.data.campaign_id}
Budget: $${campaign.budget}
${hasMessengerDestination ? `Destination: Messenger Conversation` : ''}
Status: Ready to activate`;

          setCampaignResult({
            success: true,
            message: successMessage,
            campaignId: response.data.campaign_id
          });
        }
      } else {
        // Use the legacy launch endpoint for variant-based campaigns
        const launchData = {
          name: campaign.name || `${selectedPreset.brand}_Campaign`,
          budget: (campaign.budget || selectedPreset.defaultBudget) * 100, // Convert to cents
          budgetType: campaign.budgetType || 'lifetime', // Include budget type
          start: campaign.startDate!,
          end: endDate,
          variant_ids: selectedVariants,
          client_id: clientId,
          // Include campaign type information for proper objective/optimization mapping
          campaign_type: campaign.campaignType,
          objective: campaign.objective,
          optimization_goal: campaign.optimizationGoal,
          // Include ROAS target for ROAS campaigns
          ...(campaign.campaignType === 'roas' && { roas_target: roasTarget }),
          // Include specific conversion event instead of entire conversion_tracking object
          ...(selectedConversionEvent && { conversion_event: selectedConversionEvent }),
          // Include cost per result goal if set, or auto-set for conversion campaigns with messenger destination
          ...(campaign.bid_control && { bid_control: campaign.bid_control }),
          // Auto-set bid_control for conversion campaigns with messenger destination (to match manual Facebook campaigns)
          ...(!campaign.bid_control && 
              ['purchase', 'conversions', 'add_to_cart'].includes(campaign.campaignType || '') && 
              campaign.messengerDestination === 'page' && 
              { bid_control: 500 }),
          // Include UTM template from client preset
          utmTemplate: selectedPreset.utmTemplate,
          // Include Instagram account ID if available
          instagram_account_id: selectedPreset.instagramAccountId,
          // Include custom landing page URL if provided
          landing_page_url: campaign.landingPageUrl,
          // Include Messenger destination parameters for regular campaigns with Messenger destination
          ...(hasMessengerDestination && {
            messengerDestination: campaign.messengerDestination,
            messengerCallToAction: campaign.messengerCallToAction,
            messengerGreeting: campaign.messengerGreeting,
            messengerQuickReplies: campaign.messengerQuickReplies
          })
        };

        console.log('Launching campaign with data:', launchData);

        // Call the launch endpoint which handles multiple variants
        const response = await campaignsApi.launch(launchData);

        if (response.data) {
        const successMessage = `🎉 Campaign launched successfully!

📋 Campaign ID: ${response.data.campaign_id}
📢 Ads Created: ${response.data.ads_created}
✅ ${response.data.message}

Your campaign has been created in PAUSED status. You can review and activate it in Meta Ads Manager.`;
        
        setCampaignResult({
          success: true,
          message: successMessage,
          campaignId: response.data.campaign_id?.toString(),
          adsCreated: response.data.ads_created
        });
        
        // Reset form with preset defaults
        setCampaign({
          name: '',
          campaignType: '',
          objective: '',
          optimizationGoal: '',
          budgetType: 'lifetime',
          budget: selectedPreset.defaultBudget,
          startDate: new Date().toISOString().split('T')[0],
          bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
          specialAdCategories: [],
          advantageAudience: true,
          targeting: {
            countries: selectedPreset.defaultCountries,
            ageMin: selectedPreset.defaultAgeMin,
            ageMax: selectedPreset.defaultAgeMax,
            genders: selectedPreset.defaultGenders
          },
          creative: {
            type: 'image',
            headline: '',
            primaryText: '',
            description: '',
            callToAction: 'SHOP_NOW',
            websiteUrl: selectedPreset.website
          },
          conversionTracking: {
            pixelId: selectedPreset.pixelId,
            conversionEvents: []
          }
        });
        setSelectedVariants([]);
        setVariants([]);
        setCurrentStep(1);
        setCampaignResult(null);
        }
      }
    } catch (error: any) {
      console.error('Error launching campaign:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setCampaignResult({
        success: false,
        message: `Error launching campaign: ${errorMessage}`,
        campaignId: undefined,
        adsCreated: undefined,
        campaignsCreated: undefined,
        totalAdsCreated: undefined
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateMultipleCampaigns = async () => {
    if (!selectedPreset) {
      alert('Please select a client preset first');
      return;
    }

    if (selectedVariants.length < 2) {
      alert('Please select at least 2 ad variants for multiple campaigns');
      return;
    }

    if (!multiCampaignConfig.baseName.trim()) {
      alert('Please enter a base name for your campaigns');
      return;
    }

    setIsCreating(true);
    
    try {
      console.log('Creating multiple campaigns with selected variants:', selectedVariants);

      // Calculate end date - null for unlimited campaigns
      let endDate = multiCampaignConfig.endDate;
      if (!endDate && multiCampaignConfig.duration !== null && multiCampaignConfig.duration !== undefined && multiCampaignConfig.duration > 0) {
        const start = new Date(multiCampaignConfig.startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + multiCampaignConfig.duration);
        endDate = end.toISOString().split('T')[0];
      } else if (!endDate && multiCampaignConfig.duration === undefined && selectedPreset.defaultDuration) {
        // Fallback to preset default if duration is undefined (not explicitly set to unlimited)
        const start = new Date(multiCampaignConfig.startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + selectedPreset.defaultDuration);
        endDate = end.toISOString().split('T')[0];
      }

      const clientId = getClientId();

      // Extract first selected conversion event for multi-campaign endpoint
      const selectedConversionEvent = (campaign.conversionTracking?.conversionEvents?.length ?? 0) > 0 
        ? campaign.conversionTracking?.conversionEvents?.[0] 
        : null;

      // Use the /launch-multiple endpoint
      const launchData = {
        base_name: multiCampaignConfig.baseName,
        budget: (multiCampaignConfig.budgetPerCampaign || selectedPreset.defaultBudget) * 100, // Convert to cents
        start: multiCampaignConfig.startDate,
        end: endDate,
        variant_ids: selectedVariants,
        client_id: clientId,
        num_campaigns: multiCampaignConfig.numCampaigns,
        distribution_method: multiCampaignConfig.distributionMethod,
        // Include campaign type information for proper objective/optimization mapping
        campaign_type: campaign.campaignType,
        objective: campaign.objective,
        optimization_goal: campaign.optimizationGoal,
        // Include ROAS target for ROAS campaigns
        ...(campaign.campaignType === 'roas' && { roas_target: roasTarget }),
        // Include specific conversion event instead of entire conversion_tracking object
        ...(selectedConversionEvent && { conversion_event: selectedConversionEvent }),
        // Include cost per result goal if set, or auto-set for conversion campaigns with messenger destination
        ...(campaign.bid_control && { bid_control: campaign.bid_control }),
        // Auto-set bid_control for conversion campaigns with messenger destination (to match manual Facebook campaigns)
        ...(!campaign.bid_control && 
            ['purchase', 'conversions', 'add_to_cart'].includes(campaign.campaignType || '') && 
            campaign.messengerDestination === 'page' && 
            { bid_control: 500 }),
        // Include UTM template from client preset
        utmTemplate: selectedPreset.utmTemplate,
        // Include Instagram account ID if available
        instagram_account_id: selectedPreset.instagramAccountId,
        // Include custom landing page URL if provided
        landing_page_url: campaign.landingPageUrl
      };

      console.log('Launching multiple campaigns with data:', launchData);

      // Call the launch-multiple endpoint
      const response = await campaignsApi.launchMultiple(launchData);

      if (response.data) {
        const successMessage = `🎉 Multiple campaigns launched successfully!

📋 Campaigns Created: ${response.data.campaigns_created}
📢 Total Ads Created: ${response.data.total_ads_created}
✅ ${response.data.message}

Your campaigns have been created in PAUSED status. You can review and activate them in Meta Ads Manager.`;
        
        setCampaignResult({
          success: true,
          message: successMessage,
          campaignsCreated: response.data.campaigns_created,
          totalAdsCreated: response.data.total_ads_created
        });
        
        // Reset form with preset defaults
        setCampaign({
          name: '',
          campaignType: '',
          objective: '',
          optimizationGoal: '',
          budgetType: 'lifetime',
          budget: selectedPreset.defaultBudget,
          startDate: new Date().toISOString().split('T')[0],
          bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
          specialAdCategories: [],
          advantageAudience: true,
          targeting: {
            countries: selectedPreset.defaultCountries,
            ageMin: selectedPreset.defaultAgeMin,
            ageMax: selectedPreset.defaultAgeMax,
            genders: selectedPreset.defaultGenders
          },
          creative: {
            type: 'image',
            headline: '',
            primaryText: '',
            description: '',
            callToAction: 'SHOP_NOW',
            websiteUrl: selectedPreset.website
          },
          conversionTracking: {
            pixelId: selectedPreset.pixelId,
            conversionEvents: []
          }
        });
        setMultiCampaignConfig({
          baseName: '',
          numCampaigns: 2,
          budgetPerCampaign: selectedPreset.defaultBudget,
          distributionMethod: 'round_robin',
          startDate: new Date().toISOString().split('T')[0],
          endDate: '',
          duration: selectedPreset.defaultDuration as number | null
        });
        setSelectedVariants([]);
        setVariants([]);
        setCurrentStep(1);
        setIsMultiCampaignMode(false);
        setCampaignResult(null);
      }
    } catch (error: any) {
      console.error('Error launching multiple campaigns:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setCampaignResult({
        success: false,
        message: `Error launching multiple campaigns: ${errorMessage}`,
        campaignId: undefined,
        adsCreated: undefined,
        campaignsCreated: undefined,
        totalAdsCreated: undefined
      });
    } finally {
      setIsCreating(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (isExistingCampaignMode) return !!(selectedPreset && selectedExistingCampaignId);
        return !!(selectedPreset && campaign.campaignType);
      case 2: return campaign.budget && campaign.budget > 0;
      case 3: return campaign.targeting?.countries && campaign.targeting.countries.length > 0;
      case 4: return true; // Conversion events are optional
      case 5: return selectedVariants.length > 0;
      case 6: return true;
      default: return false;
    }
  };

  // Helper function to extract database client ID from preset
  const getClientId = (): number => {
    if (!selectedPreset) return 4; // fallback
    
    // Check if it's a database client (format: "db-client-{id}")
    if (selectedPreset.id.startsWith('db-client-')) {
      const clientId = parseInt(selectedPreset.id.replace('db-client-', ''));
      return clientId || 4; // fallback to 4 if parsing fails
    }
    
    // For local presets, use default client ID 4
    return 4;
  };

  // Fetch ad variants when entering step 4
  const fetchVariants = async () => {
    if (!selectedPreset) return;
    
    setLoadingVariants(true);
    try {
      console.log('Fetching approved variants...');
      
      const clientId = getClientId();
      
      console.log(`Fetching approved variants for client ${clientId}...`);
      const response = await assetsApi.getApprovedVariants(clientId);
      console.log('Approved variants response:', response.data);
      
      if (response.data && response.data.variants) {
        setVariants(response.data.variants);
        console.log(`Loaded ${response.data.variants.length} approved variants`);
      } else {
        console.log('No approved variants found');
        setVariants([]);
      }
    } catch (error: any) {
      console.error('Error fetching approved variants:', error);
      
      // Show user-friendly error message
      if (error.response?.status === 404) {
        console.log('No approved variants found - showing empty state');
        setVariants([]);
      } else if (error.response?.status === 401) {
        console.error('Authentication error while fetching variants');
        setVariants([]);
      } else {
        console.error('Network or server error while fetching variants');
        setVariants([]);
      }
    } finally {
      setLoadingVariants(false);
    }
  };

  // Load variants when reaching step 5
  useEffect(() => {
    if (currentStep === 5 && variants.length === 0) {
      fetchVariants();
    }
  }, [currentStep]);

  const handleVariantSelect = (variantId: number) => {
    setSelectedVariants(prev => 
      prev.includes(variantId)
        ? prev.filter(id => id !== variantId)
        : [...prev, variantId]
    );
  };

  const handleSelectAllVariants = () => {
    if (selectedVariants.length === variants.length) {
      setSelectedVariants([]);
    } else {
      setSelectedVariants(variants.map(v => v.id));
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-systemGroupedBackground)', padding: 'var(--spacing-8) 0' }}>
      <div className="campaign-builder-container">
        {/* Header */}
        <div className="campaign-builder-header">
          <h1 className="campaign-builder-title">Advanced Campaign Builder</h1>
          <p className="campaign-builder-subtitle">
            Create professional Meta advertising campaigns with advanced targeting and optimization options
          </p>
        </div>

        {/* Client Selection Warning */}
        {!selectedPreset && (
          <div className="client-selection-warning">
            <h2>⚠️ No Client Selected</h2>
            <p>
              Please select a client preset to continue. Client presets contain essential configuration 
              like Meta account credentials, page information, and default settings.
            </p>
            <div className="client-selection-grid">
              {presets.map(preset => (
                <div
                  key={preset.id}
                  onClick={() => selectPreset(preset.id)}
                  className="client-selection-card"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '4px',
                        backgroundColor: preset.primaryColor
                      }}
                    />
                    <strong>{preset.brand}</strong>
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', opacity: 0.8 }}>
                    {preset.industry} • ${preset.defaultBudget} budget
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Client Banner */}
        {selectedPreset && (
          <div className="selected-client-banner">
            <div className="selected-client-content">
              <div className="selected-client-info">
                <h2>🎯 Building Campaign for {selectedPreset.brand}</h2>
                <p>
                  Account: {selectedPreset.metaAccountId} • Page: {selectedPreset.pageName} • Pixel: {selectedPreset.pixelId}
                </p>
              </div>
              <button
                onClick={() => selectPreset('')}
                className="selected-client-change-btn"
              >
                Change Client
              </button>
            </div>
          </div>
        )}

        {/* Campaign Result Display */}
        {campaignResult && (
          <div style={{
            margin: 'var(--spacing-6) 0',
            padding: 'var(--spacing-6)',
            borderRadius: '16px',
            border: '2px solid',
            borderColor: campaignResult.success ? 'var(--color-success)' : 'var(--color-error)',
            background: campaignResult.success 
              ? 'var(--color-successSubtle)' 
              : 'var(--color-errorSubtle)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setCampaignResult(null)}
              style={{
                position: 'absolute',
                top: 'var(--spacing-3)',
                right: 'var(--spacing-3)',
                background: 'none',
                border: 'none',
                fontSize: 'var(--font-size-lg)',
                cursor: 'pointer',
                color: 'var(--color-secondaryLabel)',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-systemFill)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              ×
            </button>

            {/* Success Result */}
            {campaignResult.success && (
              <div>
                <h3 style={{
                  color: 'var(--color-success)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  marginBottom: 'var(--spacing-4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)'
                }}>
                  🎉 Campaign Launched Successfully!
                </h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--spacing-4)',
                  marginBottom: 'var(--spacing-4)'
                }}>
                  {/* Campaign ID */}
                  {campaignResult.campaignId && (
                    <div style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-card)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-border)'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-label)',
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        📋 Campaign ID
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-2)'
                      }}>
                        <code style={{
                          flex: 1,
                          padding: 'var(--spacing-2)',
                          background: 'var(--color-systemFill)',
                          borderRadius: '6px',
                          fontSize: 'var(--font-size-sm)',
                          fontFamily: 'monaco, monospace',
                          color: 'var(--color-label)',
                          userSelect: 'all'
                        }}>
                          {campaignResult.campaignId}
                        </code>
                        <button
                          onClick={(event) => {
                            navigator.clipboard.writeText(campaignResult.campaignId!);
                            // Brief visual feedback
                            const btn = event.target as HTMLButtonElement;
                            const originalText = btn.textContent;
                            btn.textContent = '✓';
                            setTimeout(() => { btn.textContent = originalText; }, 1000);
                          }}
                          style={{
                            padding: 'var(--spacing-1) var(--spacing-2)',
                            background: 'var(--color-primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: 'var(--font-size-xs)',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                        >
                          📋 Copy
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Ads Created */}
                  {campaignResult.adsCreated && (
                    <div style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-card)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-border)'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-label)',
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        📢 Ads Created
                      </label>
                      <span style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-primary)'
                      }}>
                        {campaignResult.adsCreated}
                      </span>
                    </div>
                  )}

                  {/* Multiple Campaigns */}
                  {campaignResult.campaignsCreated && (
                    <div style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-card)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-border)'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-label)',
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        🚀 Campaigns Created
                      </label>
                      <span style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-primary)'
                      }}>
                        {campaignResult.campaignsCreated}
                      </span>
                    </div>
                  )}

                  {/* Total Ads (Multiple Campaigns) */}
                  {campaignResult.totalAdsCreated && (
                    <div style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-card)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-border)'
                    }}>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-label)',
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        📢 Total Ads Created
                      </label>
                      <span style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-primary)'
                      }}>
                        {campaignResult.totalAdsCreated}
                      </span>
                    </div>
                  )}
                </div>

                <div style={{
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-successSubtle)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-successBorder)',
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-normal)'
                }}>
                  ✅ Your campaign{campaignResult.campaignsCreated ? 's have' : ' has'} been created in <strong>PAUSED</strong> status. 
                  You can review and activate {campaignResult.campaignsCreated ? 'them' : 'it'} in Meta Ads Manager.
                </div>
              </div>
            )}

            {/* Error Result */}
            {!campaignResult.success && (
              <div>
                <h3 style={{
                  color: 'var(--color-error)',
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  marginBottom: 'var(--spacing-4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)'
                }}>
                  ❌ Campaign Creation Failed
                </h3>

                <div style={{
                  padding: 'var(--spacing-4)',
                  background: 'var(--color-errorSubtle)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-errorBorder)',
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-sm)',
                  lineHeight: 'var(--line-height-normal)'
                }}>
                  <strong>Error Details:</strong><br />
                  {campaignResult.message}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step Progress Indicator */}
        {selectedPreset && (
          <div className="step-progress-container">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="step-progress-item">
                  <div className={`step-progress-circle ${
                    currentStep === step.id ? 'active' : 
                    currentStep > step.id ? 'completed' : ''
                  }`}>
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <div className="step-progress-info">
                    <div className={`step-progress-title ${currentStep === step.id ? 'active' : ''}`}>
                      {step.title}
                    </div>
                    <div className="step-progress-description">
                      {step.description}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`step-progress-line ${currentStep > step.id ? 'completed' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step Content */}
        {selectedPreset && (
          <>
            {/* Step 1: Campaign Type Selection */}
            {currentStep === 1 && (
              <div>
                {/* Mode Toggle: New vs Existing Campaign */}
                <div className="form-section">
                  <h3 className="form-section-title">Campaign Mode</h3>
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)' }}>
                    <button
                      onClick={() => {
                        setIsExistingCampaignMode(false);
                        setSelectedExistingCampaignId('');
                      }}
                      style={{
                        flex: 1,
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderRadius: 'var(--radius-card)',
                        border: `2px solid ${!isExistingCampaignMode ? 'var(--color-primary)' : 'var(--color-border)'}`,
                        background: !isExistingCampaignMode ? 'var(--color-primary)' : 'var(--color-card)',
                        color: !isExistingCampaignMode ? 'white' : 'var(--color-label)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-base)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      🆕 Create New Campaign
                    </button>
                    <button
                      onClick={() => {
                        setIsExistingCampaignMode(true);
                        if (selectedPreset && existingMetaCampaigns.length === 0) {
                          fetchExistingMetaCampaigns();
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderRadius: 'var(--radius-card)',
                        border: `2px solid ${isExistingCampaignMode ? 'var(--color-primary)' : 'var(--color-border)'}`,
                        background: isExistingCampaignMode ? 'var(--color-primary)' : 'var(--color-card)',
                        color: isExistingCampaignMode ? 'white' : 'var(--color-label)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-base)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      📂 Add to Existing Campaign
                    </button>
                  </div>
                </div>

                {/* Existing Campaign Selector */}
                {isExistingCampaignMode && (
                  <div className="form-section">
                    <h3 className="form-section-title">Select Existing Campaign</h3>
                    <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-2)' }}>
                      New adsets and ads will be created inside the selected campaign. Budget, targeting, and ad creative settings will apply to the new adset.
                    </p>
                    {loadingExistingCampaigns ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-4)', color: 'var(--color-secondaryLabel)' }}>
                        <div className="loading-spinner" />
                        Loading campaigns from Meta…
                      </div>
                    ) : existingMetaCampaigns.length === 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', alignItems: 'flex-start' }}>
                        <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)' }}>No campaigns loaded yet.</p>
                        <button
                          onClick={fetchExistingMetaCampaigns}
                          className="btn btn-secondary btn-sm"
                        >
                          🔄 Load Campaigns
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1\.5)' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--spacing-1)' }}>
                          <button onClick={fetchExistingMetaCampaigns} className="btn btn-secondary btn-sm">
                            🔄 Refresh
                          </button>
                        </div>
                        {existingMetaCampaigns.map(camp => (
                          <div
                            key={camp.id}
                            onClick={() => {
                              setSelectedExistingCampaignId(camp.id);
                              setCampaign(prev => ({ ...prev, name: `${camp.name}_AdSet` }));
                            }}
                            style={{
                              padding: 'var(--spacing-2) var(--spacing-3)',
                              borderRadius: 'var(--radius-card)',
                              border: `2px solid ${selectedExistingCampaignId === camp.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                              background: selectedExistingCampaignId === camp.id ? 'var(--color-primaryTranslucent)' : 'var(--color-card)',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 'var(--spacing-2)'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--color-label)', fontSize: 'var(--font-size-base)' }}>
                                {camp.name}
                              </div>
                              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-secondaryLabel)', marginTop: '2px' }}>
                                ID: {camp.id} &bull; {camp.objective}
                              </div>
                            </div>
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: '20px',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 600,
                              background: camp.effective_status === 'ACTIVE' ? 'rgba(52, 199, 89, 0.15)' : 'rgba(120,120,128,0.12)',
                              color: camp.effective_status === 'ACTIVE' ? 'var(--color-systemGreen)' : 'var(--color-secondaryLabel)'
                            }}>
                              {camp.effective_status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* New Campaign: Category + Type Selection */}
                {!isExistingCampaignMode && (
                <>
                {/* Category Selection */}
                <div className="form-section">
                  <h3 className="form-section-title">Choose Campaign Category</h3>
                  <div className="campaign-categories-grid">
                    {campaignCategories.map(category => (
                      <div
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`campaign-category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                        data-category={category.id}
                      >
                        <span className="campaign-category-icon">{category.icon}</span>
                        <h4 className="campaign-category-name">{category.name}</h4>
                        <p className="campaign-category-description">{category.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Campaign Type Selection */}
                {selectedCategory && (
                  <div className="form-section">
                    <h3 className="form-section-title">Select Campaign Type</h3>
                    <div className="campaign-types-grid">
                      {campaignTypes
                        .filter(type => type.category === selectedCategory)
                        .map(type => (
                          <div
                            key={type.id}
                            onClick={() => handleCampaignTypeSelect(type)}
                            className={`campaign-type-card ${campaign.campaignType === type.id ? 'selected' : ''}`}
                            data-category={type.category}
                          >
                            <div className="campaign-type-header">
                              <span className="campaign-type-icon">{type.icon}</span>
                              <h4 className="campaign-type-name">{type.name}</h4>
                            </div>
                            <p className="campaign-type-description">{type.description}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Messenger-specific configuration */}
                {campaign.campaignType === 'click_to_messenger' && (
                  <div className="form-section messenger-config">
                    <h3 className="form-section-title">💬 Messenger Configuration</h3>
                    <div className="form-grid two-columns">
                      <div className="form-group">
                        <label className="form-label">Conversation Destination</label>
                        <select
                          value={campaign.messengerDestination || 'page'}
                          onChange={(e) => setCampaign(prev => ({ 
                            ...prev, 
                            messengerDestination: e.target.value as 'page' | 'app' | 'website'
                          }))}
                          className="form-select"
                        >
                          <option value="page">Facebook Page (Recommended)</option>
                          <option value="website">Website with Messenger</option>
                          <option value="app">Mobile App</option>
                        </select>
                        <small className="form-help">Where users will be directed when they click your ad</small>
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Campaign Template</label>
                        <select
                          value={campaign.messengerTemplate || 'lead_generation'}
                          onChange={(e) => setCampaign(prev => ({ 
                            ...prev, 
                            messengerTemplate: e.target.value as 'lead_generation' | 'customer_support' | 'appointment_booking'
                          }))}
                          className="form-select"
                        >
                          <option value="lead_generation">Lead Generation</option>
                          <option value="customer_support">Customer Support</option>
                          <option value="appointment_booking">Appointment Booking</option>
                        </select>
                        <small className="form-help">Choose the template that best fits your goals</small>
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Call-to-Action Button</label>
                        <select
                          value={campaign.messengerCallToAction || 'Send Message'}
                          onChange={(e) => setCampaign(prev => ({ 
                            ...prev, 
                            messengerCallToAction: e.target.value
                          }))}
                          className="form-select"
                        >
                          <option value="Send Message">Send Message</option>
                          <option value="Get Quote">Get Quote</option>
                          <option value="Contact Us">Contact Us</option>
                          <option value="Learn More">Learn More</option>
                          <option value="Book Now">Book Now</option>
                          <option value="Apply Now">Apply Now</option>
                        </select>
                        <small className="form-help">Text that appears on your ad button</small>
                      </div>
                      
                      <div className="form-group full-width">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                          <label className="form-label">Welcome Message (Optional)</label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                            <input
                              type="checkbox"
                              checked={!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Use default - clear custom greeting
                                  setCampaign(prev => ({ ...prev, messengerGreeting: '' }));
                                } else {
                                  // Use custom - set to current default or empty
                                  setCampaign(prev => ({ 
                                    ...prev, 
                                    messengerGreeting: selectedPreset?.defaultMessengerGreeting || ''
                                  }));
                                }
                              }}
                            />
                            <span>Use Default</span>
                          </label>
                        </div>
                        <textarea
                          value={campaign.messengerGreeting || selectedPreset?.defaultMessengerGreeting || ''}
                          onChange={(e) => setCampaign(prev => ({ 
                            ...prev, 
                            messengerGreeting: e.target.value
                          }))}
                          className="form-textarea"
                          placeholder={selectedPreset?.defaultMessengerGreeting || "Hi {{user_first_name}}! Thanks for your interest. How can I help you today?"}
                          rows={3}
                          disabled={!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting}
                          style={{
                            opacity: (!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting) ? 0.7 : 1,
                            cursor: (!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting) ? 'not-allowed' : 'text'
                          }}
                        />
                        <small className="form-help">This message will be sent when users start a conversation (optional)</small>
                        <div style={{
                          marginTop: 'var(--spacing-2)',
                          padding: 'var(--spacing-2)',
                          background: 'var(--color-systemGray6)',
                          borderRadius: 'var(--border-radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-secondaryLabel)'
                        }}>
                          <strong>Available Macros:</strong><br/>
                          • <code>{`{{user_first_name}}`}</code> - User's first name (Facebook)<br/>
                          • <code>{`{{ad_name}}`}</code> - Your ad name (Custom)<br/>
                          • <code>{`{{ad_headline}}`}</code> - Your ad headline (Custom)<br/>
                          • <code>{`{{ad_body}}`}</code> - Your ad body text (Custom)
                        </div>
                        
                        {/* Quick Replies Configuration */}
                        <div style={{ marginTop: 'var(--spacing-4)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                            <label className="form-label">Quick Reply Buttons (Optional)</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                              <input
                                type="checkbox"
                                checked={!campaign.messengerQuickReplies || JSON.stringify(campaign.messengerQuickReplies) === JSON.stringify(selectedPreset?.defaultMessengerQuickReplies)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Use default - clear custom quick replies
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: undefined }));
                                  } else {
                                    // Use custom - set to current default or empty array
                                    setCampaign(prev => ({ 
                                      ...prev, 
                                      messengerQuickReplies: selectedPreset?.defaultMessengerQuickReplies || []
                                    }));
                                  }
                                }}
                              />
                              <span>Use Default</span>
                            </label>
                          </div>
                          <div style={{ 
                            border: '1px solid var(--color-border)', 
                            borderRadius: 'var(--border-radius-md)', 
                            padding: 'var(--spacing-3)',
                            background: 'var(--color-card)',
                            opacity: (!campaign.messengerQuickReplies || JSON.stringify(campaign.messengerQuickReplies) === JSON.stringify(selectedPreset?.defaultMessengerQuickReplies)) ? 0.7 : 1
                          }}>
                            {(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || []).map((reply, index) => (
                              <div key={index} style={{ 
                                display: 'flex', 
                                gap: 'var(--spacing-2)', 
                                marginBottom: 'var(--spacing-2)',
                                alignItems: 'center'
                              }}>
                                <input
                                  type="text"
                                  placeholder="Button text (max 20 chars)"
                                  value={reply.title}
                                  maxLength={20}
                                  onChange={(e) => {
                                    const newReplies = [...(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [])];
                                    newReplies[index] = { ...reply, title: e.target.value };
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: 'var(--spacing-2)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--border-radius-sm)',
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                />
                                <input
                                  type="text"
                                  placeholder="Payload (internal identifier)"
                                  value={reply.payload}
                                  onChange={(e) => {
                                    const newReplies = [...(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [])];
                                    newReplies[index] = { ...reply, payload: e.target.value };
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: 'var(--spacing-2)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--border-radius-sm)',
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newReplies = [...(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [])];
                                    newReplies.splice(index, 1);
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                  }}
                                  style={{
                                    padding: 'var(--spacing-1)',
                                    background: 'var(--color-error)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--border-radius-sm)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            
                            {(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || []).length < 13 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const currentReplies = campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [];
                                  const newReplies = [...currentReplies, { title: '', payload: '' }];
                                  setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                }}
                                style={{
                                  padding: 'var(--spacing-2)',
                                  background: 'var(--color-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 'var(--border-radius-sm)',
                                  cursor: 'pointer',
                                  fontSize: 'var(--font-size-sm)',
                                  width: '100%'
                                }}
                              >
                                + Add Quick Reply Button
                              </button>
                            )}
                            
                            <small style={{ 
                              display: 'block', 
                              marginTop: 'var(--spacing-2)', 
                              color: 'var(--color-secondaryLabel)',
                              fontSize: 'var(--font-size-xs)'
                            }}>
                              Quick reply buttons help users respond quickly. Max 13 buttons, 20 characters each.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </>
                )}
              </div>
            )}

            {/* Step 2: Budget & Schedule */}
            {currentStep === 2 && (
              <div className="form-section">
                <h3 className="form-section-title">Budget & Schedule Configuration</h3>
                <div className="form-grid two-columns">
                  <div className="form-group">
                    <label className="form-label">Campaign Name</label>
                    <input
                      type="text"
                      value={campaign.name || ''}
                      onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                      className="form-input"
                      placeholder="Enter campaign name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Budget Type</label>
                    <select
                      value={campaign.budgetType || 'lifetime'}
                      onChange={(e) => setCampaign(prev => ({ ...prev, budgetType: e.target.value as 'daily' | 'lifetime' }))}
                      className="form-select"
                    >
                      <option value="lifetime">Lifetime Budget</option>
                      <option value="daily">Daily Budget</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Budget Amount ($)</label>
                    <input
                      type="number"
                      value={campaign.budget || selectedPreset?.defaultBudget || 0}
                      onChange={(e) => setCampaign(prev => ({ ...prev, budget: parseInt(e.target.value) }))}
                      className="form-input"
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cost per result goal ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="e.g., 5.00, 10.00, 50.00"
                      value={campaign.bid_control ? (campaign.bid_control / 100).toFixed(2) : ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setCampaign(prev => ({ 
                          ...prev, 
                          bid_control: isNaN(value) ? undefined : Math.round(value * 100) 
                        }));
                      }}
                      className="form-input"
                    />
                    <small className="form-help">
                      Optional. Set a target cost per result (e.g., $1.50, $10.00, $50.00). Leave empty for automatic bidding.
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      value={campaign.startDate || ''}
                      onChange={(e) => setCampaign(prev => ({ ...prev, startDate: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Campaign Duration</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="durationType"
                            checked={campaign.duration !== null && campaign.duration !== undefined}
                            onChange={() => setCampaign(prev => ({ ...prev, duration: selectedPreset?.defaultDuration || 7 }))}
                          />
                          <span>Limited Duration</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="durationType"
                            checked={campaign.duration === null || campaign.duration === undefined}
                            onChange={() => setCampaign(prev => ({ ...prev, duration: null }))}
                          />
                          <span>Unlimited/Ongoing</span>
                        </label>
                      </div>
                      {(campaign.duration !== null && campaign.duration !== undefined) && (
                        <input
                          type="number"
                          value={campaign.duration || selectedPreset?.defaultDuration || 7}
                          onChange={(e) => setCampaign(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                          className="form-input"
                          min="1"
                          max="365"
                          placeholder="Days"
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bid Strategy</label>
                    <select
                      value={campaign.bidStrategy || 'LOWEST_COST_WITHOUT_CAP'}
                      onChange={(e) => setCampaign(prev => ({ ...prev, bidStrategy: e.target.value }))}
                      className="form-select"
                    >
                      {bidStrategies.map(strategy => (
                        <option key={strategy.id} value={strategy.id}>
                          {strategy.name} - {strategy.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* ROAS Target - Only show for ROAS campaigns */}
                  {campaign.campaignType === 'roas' && (
                    <div className="form-group">
                      <label className="form-label">
                        Target ROAS (Return on Ad Spend)
                        <span style={{ 
                          fontSize: 'var(--font-size-xs)', 
                          color: 'var(--color-secondaryLabel)',
                          fontWeight: 'normal',
                          marginLeft: 'var(--spacing-2)'
                        }}>
                          Example: 3.0 = $3 revenue for every $1 spent
                        </span>
                      </label>
                      <input
                        type="number"
                        value={roasTarget}
                        onChange={(e) => setRoasTarget(parseFloat(e.target.value))}
                        className="form-input"
                        min="1.0"
                        max="20.0"
                        step="0.1"
                        placeholder="1.6"
                      />
                      <div style={{
                        marginTop: 'var(--spacing-2)',
                        padding: 'var(--spacing-3)',
                        background: 'var(--color-primaryTranslucent)',
                        borderRadius: '8px',
                        border: '1px solid var(--color-primaryBorder)'
                      }}>
                        <p style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-label)',
                          margin: 0,
                          lineHeight: 'var(--line-height-normal)'
                        }}>
                          💡 <strong>ROAS Target:</strong> Meta will optimize your campaign to achieve this return on ad spend. 
                          Higher targets may limit reach but improve profitability. Start with 1.6-4.0 for most businesses.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Targeting */}
            {currentStep === 3 && (
              <div>
                <div className="form-section">
                  <h3 className="form-section-title">Geographic & Demographic Targeting</h3>
                  <div className="form-grid two-columns">
                    <div className="form-group">
                      <label className="form-label">Age Range</label>
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                        <input
                          type="number"
                          value={campaign.targeting?.ageMin || selectedPreset?.defaultAgeMin || 18}
                          onChange={(e) => handleTargetingChange('ageMin', parseInt(e.target.value))}
                          className="form-input"
                          placeholder="Min"
                          min="13"
                          max="65"
                        />
                        <input
                          type="number"
                          value={campaign.targeting?.ageMax || selectedPreset?.defaultAgeMax || 65}
                          onChange={(e) => handleTargetingChange('ageMax', parseInt(e.target.value))}
                          className="form-input"
                          placeholder="Max"
                          min="13"
                          max="65"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Gender Targeting</label>
                      <div style={{ 
                        display: 'flex', 
                        gap: 'var(--spacing-4)',
                        padding: 'var(--spacing-3)',
                        background: 'var(--color-systemFill)',
                        borderRadius: '8px'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                          <input
                            type="checkbox"
                            checked={campaign.targeting?.genders?.includes(1) || false}
                            onChange={(e) => {
                              const genders = campaign.targeting?.genders || [];
                              if (e.target.checked) {
                                handleTargetingChange('genders', [...genders.filter(g => g !== 1), 1]);
                              } else {
                                handleTargetingChange('genders', genders.filter(g => g !== 1));
                              }
                            }}
                          />
                          <span>Male</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                          <input
                            type="checkbox"
                            checked={campaign.targeting?.genders?.includes(2) || false}
                            onChange={(e) => {
                              const genders = campaign.targeting?.genders || [];
                              if (e.target.checked) {
                                handleTargetingChange('genders', [...genders.filter(g => g !== 2), 2]);
                              } else {
                                handleTargetingChange('genders', genders.filter(g => g !== 2));
                              }
                            }}
                          />
                          <span>Female</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Countries */}
                  <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <label className="form-label" style={{ marginBottom: 'var(--spacing-3)' }}>
                      Target Countries
                    </label>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: 'var(--spacing-2)',
                      padding: 'var(--spacing-4)',
                      background: 'var(--color-systemFill)',
                      borderRadius: '12px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {countries.map(country => (
                        <label 
                          key={country.code} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-2)',
                            cursor: 'pointer',
                            padding: 'var(--spacing-1)',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-secondarySystemFill)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={campaign.targeting?.countries?.includes(country.code) || false}
                            onChange={() => handleCountryToggle(country.code)}
                            style={{ margin: 0 }}
                          />
                          <span>{country.flag}</span>
                          <span style={{ fontSize: 'var(--font-size-sm)' }}>{country.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Step 4: Conversion Events */}
            {currentStep === 4 && (
              <div>
                <div className="form-section">
                  <h3 className="form-section-title">Conversion Events</h3>
                  <p style={{ 
                    color: 'var(--color-secondaryLabel)', 
                    marginBottom: 'var(--spacing-4)',
                    fontSize: 'var(--font-size-base)'
                  }}>
                    Select the conversion events you want to track
                  </p>

                  {/* Conversion Events Selection */}
                  <div style={{
                    marginBottom: 'var(--spacing-6)',
                    padding: 'var(--spacing-4)',
                    background: 'var(--color-systemFill)',
                    borderRadius: '16px',
                    border: '1px solid var(--color-border)'
                  }}>
                    <h4 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-label)',
                      marginBottom: 'var(--spacing-3)'
                    }}>
                      📋 Conversion Events
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 'var(--spacing-3)'
                    }}>
                      {conversionEvents.map((event) => (
                        <label 
                          key={event.id} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-2)',
                            cursor: 'pointer',
                            padding: 'var(--spacing-2)',
                            borderRadius: '8px',
                            border: campaign.conversionTracking?.conversionEvents?.includes(event.id) 
                              ? '2px solid var(--color-primary)' 
                              : '2px solid transparent',
                            background: campaign.conversionTracking?.conversionEvents?.includes(event.id) 
                              ? 'var(--color-primaryTranslucent)' 
                              : 'var(--color-card)',
                            transition: 'all 0.2s'
                          }}
                          onClick={() => handleConversionEventToggle(event.id)}
                        >
                          <input
                            type="checkbox"
                            checked={campaign.conversionTracking?.conversionEvents?.includes(event.id) || false}
                            onChange={() => {}}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                            {event.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Ad Variations */}
            {currentStep === 5 && (
              <div>
                {/* Check if this is a specialized Messenger campaign (not Click-to-Messenger) */}
                {campaign.campaignType === 'click_to_messenger' ? (
                  <div className="form-section">
                    <h3 className="form-section-title">💬 Messenger Campaign Assets (Optional)</h3>
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                      border: '2px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: 'var(--border-radius-lg)',
                      padding: 'var(--spacing-6)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-4)' }}>💬</div>
                      <h4 style={{ 
                        fontSize: 'var(--font-size-xl)', 
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-primary)',
                        marginBottom: 'var(--spacing-3)'
                      }}>
                        Messenger Campaign Ready!
                      </h4>
                      <p style={{ 
                        color: 'var(--color-secondaryLabel)', 
                        fontSize: 'var(--font-size-lg)',
                        lineHeight: '1.6',
                        maxWidth: '600px',
                        margin: '0 auto var(--spacing-4)'
                      }}>
                        Messenger campaigns use simple text-based ads that connect users directly to your Facebook Page conversation. 
                        No additional assets are required - your campaign is configured and ready to launch!
                      </p>
                      <div style={{
                        background: 'var(--color-systemBackground)',
                        borderRadius: '12px',
                        padding: 'var(--spacing-4)',
                        margin: 'var(--spacing-4) auto 0',
                        maxWidth: '500px',
                        border: '1px solid var(--color-border)'
                      }}>
                        <p style={{ 
                          color: 'var(--color-label)', 
                          fontSize: 'var(--font-size-base)',
                          fontWeight: 'var(--font-weight-semibold)',
                          marginBottom: 'var(--spacing-2)'
                        }}>
                          Your Messenger Campaign:
                        </p>
                        <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)' }}>
                          <strong>Type:</strong> {campaignTypes.find(t => t.id === campaign.campaignType)?.name}<br/>
                          <strong>Destination:</strong> {campaign.messengerDestination || 'Facebook Page'}<br/>
                          <strong>Button:</strong> {campaign.messengerCallToAction || 'Send Message'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="form-section">
                    <h3 className="form-section-title">Ad Variations</h3>
                    <p style={{ 
                      color: 'var(--color-secondaryLabel)', 
                      marginBottom: 'var(--spacing-4)',
                      fontSize: 'var(--font-size-base)'
                    }}>
                      Select from your previously created ad variants or upload new assets to create more variations
                    </p>

                  {/* Upload New Assets Section */}
                  <div style={{
                    marginBottom: 'var(--spacing-6)',
                    padding: 'var(--spacing-4)',
                    background: 'var(--color-systemFill)',
                    borderRadius: '16px',
                    border: '1px solid var(--color-border)'
                  }}>
                    <h4 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-label)',
                      marginBottom: 'var(--spacing-3)'
                    }}>
                      📸 Upload New Assets
                    </h4>
                    <p style={{
                      color: 'var(--color-secondaryLabel)',
                      fontSize: 'var(--font-size-sm)',
                      marginBottom: 'var(--spacing-3)'
                    }}>
                      Upload images or videos to automatically generate new ad variations
                    </p>
                    <button
                      onClick={() => window.open('/upload', '_blank')}
                      className="btn btn-primary"
                      style={{ marginRight: 'var(--spacing-3)' }}
                    >
                      🚀 Upload & Generate Variations
                    </button>
                    <button
                      onClick={() => fetchVariants()}
                      className="btn btn-secondary"
                      disabled={loadingVariants}
                    >
                      🔄 Refresh Variations
                    </button>
                  </div>

                  {/* Selection Summary */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-4)',
                    padding: 'var(--spacing-3)',
                    background: selectedVariants.length > 0 ? 'var(--color-secondarySystemFill)' : 'var(--color-systemFill)',
                    borderRadius: '12px',
                    border: selectedVariants.length > 0 ? '1px solid var(--color-primary)' : '1px solid var(--color-border)'
                  }}>
                    <div>
                      <span style={{
                        color: 'var(--color-label)',
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 600,
                      }}>
                        {selectedVariants.length} of {variants.length} variants selected
                      </span>
                      {selectedVariants.length > 0 && (
                        <p style={{
                          color: 'var(--color-secondaryLabel)',
                          fontSize: 'var(--font-size-sm)',
                          margin: '4px 0 0 0'
                        }}>
                          These variants will be used to create ads in your campaign
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleSelectAllVariants}
                      className="btn btn-secondary"
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {selectedVariants.length === variants.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {/* Loading State */}
                  {loadingVariants && (
                    <div style={{
                      textAlign: 'center',
                      padding: 'var(--spacing-8)',
                      color: 'var(--color-secondaryLabel)'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid var(--color-systemFill)',
                        borderTop: '3px solid var(--color-primary)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto var(--spacing-3)'
                      }} />
                      Loading your ad variants...
                    </div>
                  )}

                  {/* No Variants State */}
                  {!loadingVariants && variants.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: 'var(--spacing-8)',
                      background: 'var(--color-systemFill)',
                      borderRadius: '16px',
                      border: '2px dashed var(--color-border)'
                    }}>
                      <div style={{
                        fontSize: 'var(--font-size-5xl)',
                        marginBottom: 'var(--spacing-3)',
                        opacity: 0.5
                      }}>
                        📝
                      </div>
                      <h4 style={{
                        color: 'var(--color-label)',
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 600,
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        No Ad Variants Found
                      </h4>
                      <p style={{
                        color: 'var(--color-secondaryLabel)',
                        fontSize: 'var(--font-size-base)',
                        marginBottom: 'var(--spacing-4)',
                        maxWidth: '400px',
                        margin: '0 auto var(--spacing-4)'
                      }}>
                        You haven't created any ad variants yet. Upload assets to generate variations automatically.
                      </p>
                      <button
                        onClick={() => window.open('/upload', '_blank')}
                        className="btn btn-primary"
                      >
                        🚀 Create Your First Variations
                      </button>
                    </div>
                  )}

                  {/* Variants Grid */}
                  {!loadingVariants && variants.length > 0 && (
                    <div className="variants-grid">
                      {variants.map((variant) => (
                        <div
                          key={variant.id}
                          className={`variant-card ${selectedVariants.includes(variant.id) ? 'selected' : ''}`}
                          onClick={() => handleVariantSelect(variant.id)}
                        >
                          {/* Delete Button */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to delete this variant?')) {
                                try {
                                  await assetsApi.deleteVariant(variant.id);
                                  toast.success('Variant deleted successfully');
                                  // Remove from local state
                                  setVariants(prev => prev.filter(v => v.id !== variant.id));
                                  // Remove from selected variants if it was selected
                                  setSelectedVariants(prev => prev.filter(id => id !== variant.id));
                                } catch (error) {
                                  console.error('Error deleting variant:', error);
                                  toast.error('Failed to delete variant');
                                }
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: '12px',
                              left: '12px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              border: 'none',
                              background: 'var(--color-errorTranslucent)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              zIndex: 10,
                              transition: 'all 0.2s ease',
                            }}
                            title="Delete variant"
                          >
                            ×
                          </button>

                          {/* Selection Indicator */}
                          {selectedVariants.includes(variant.id) && (
                            <div style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: 'var(--color-primary)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 8px var(--color-primaryGlow)',
                              zIndex: 10,
                            }}>
                              ✓
                            </div>
                          )}

                          {/* Meta Ad Format: Title → Image/Video → Description */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--spacing-3)',
                            height: '100%',
                          }}>
                            {/* Title at top (like Meta ad) */}
                            <h3 style={{
                              fontSize: 'var(--font-size-base)',
                              fontWeight: 600,
                              color: 'var(--color-label)',
                              lineHeight: 1.4,
                              margin: 0,
                              padding: 'var(--spacing-2)',
                              textAlign: 'center',
                            }}>
                              {variant.headline}
                            </h3>

                            {/* Asset Preview in middle (like Meta ad) */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px',
                              background: 'var(--color-systemGray6)',
                              overflow: 'hidden',
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid var(--color-border)',
                            }}>
                              {variant.asset_url ? (
                                <>
                                  {variant.asset_type === 'video' ? (
                                    <VideoAsset
                                      src={variant.asset_url}
                                      assetId={variant.asset_id}
                                      poster={variant.asset_thumbnail_url}
                                      showPlayButton={true}
                                      autoPlayOnHover={true}
                                    />
                                  ) : (
                                    <img
                                      src={variant.asset_url || variant.asset_thumbnail_url}
                                      alt={`Variant ${variant.id}`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (fallback) {
                                          fallback.style.display = 'flex';
                                        }
                                      }}
                                    />
                                  )}
                                  {/* Fallback for broken images */}
                                  <div style={{
                                    display: 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    background: 'var(--color-systemFill)',
                                    color: 'var(--color-tertiaryLabel)',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    flexDirection: 'column',
                                    gap: '8px',
                                  }}>
                                    <span style={{ fontSize: '32px' }}>
                                      {variant.asset_type === 'video' ? '🎥' : '🖼️'}
                                    </span>
                                    <span>{variant.asset_type?.toUpperCase() || 'MEDIA'}</span>
                                  </div>
                                </>
                              ) : (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px',
                                  color: 'var(--color-tertiaryLabel)',
                                }}>
                                  <span style={{ fontSize: '48px' }}>
                                    {variant.asset_type === 'video' ? '🎥' : '📝'}
                                  </span>
                                  <span style={{ fontSize: '14px', fontWeight: 600 }}>
                                    {variant.asset_type ? variant.asset_type.toUpperCase() : 'NO PREVIEW'}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Description at bottom (like Meta ad) */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                              <p style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-secondaryLabel)',
                                lineHeight: 1.4,
                                margin: 0,
                                padding: 'var(--spacing-2)',
                                flex: 1,
                              }}>
                                {variant.body}
                              </p>

                              {/* Ad Name Field */}
                              <div style={{
                                padding: 'var(--spacing-2)',
                                borderTop: '1px solid var(--color-border)',
                                marginTop: 'var(--spacing-2)',
                              }}>
                                <label style={{
                                  display: 'block',
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--color-secondaryLabel)',
                                  marginBottom: 'var(--spacing-1)',
                                  fontWeight: 600,
                                }}>
                                  Ad Name:
                                </label>
                                <input
                                  type="text"
                                  value={variant.ad_name || variant.headline || ''}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    // Update local state
                                    setVariants(prev => prev.map(v => 
                                      v.id === variant.id 
                                        ? { ...v, ad_name: e.target.value }
                                        : v
                                    ));
                                  }}
                                  onBlur={async (e) => {
                                    // Save to backend when user finishes editing
                                    try {
                                      await assetsApi.updateVariant(variant.id, {
                                        ad_name: e.target.value
                                      });
                                    } catch (error) {
                                      console.error('Failed to update ad name:', error);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                    padding: 'var(--spacing-1)',
                                    fontSize: 'var(--font-size-xs)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: '4px',
                                    background: 'var(--color-systemBackground)',
                                    color: 'var(--color-label)',
                                  }}
                                  placeholder="Enter custom ad name..."
                                />
                              </div>

                              {/* Meta info at very bottom */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-2)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-tertiaryLabel)',
                                borderTop: '1px solid var(--color-border)',
                              }}>
                                <span>ID: {variant.id}</span>
                                <span>
                                  {variant.is_approved ? '✅ Approved' : '⏳ Pending'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Variants Selection Help */}
                  {!loadingVariants && variants.length > 0 && (
                    <div style={{
                      marginTop: 'var(--spacing-6)',
                      padding: 'var(--spacing-4)',
                      background: 'var(--color-primaryTranslucent)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-primaryBorder)'
                    }}>
                      <h4 style={{
                        color: 'var(--color-primary)',
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 600,
                        marginBottom: 'var(--spacing-2)'
                      }}>
                        💡 Pro Tip
                      </h4>
                      <p style={{
                        color: 'var(--color-label)',
                        fontSize: 'var(--font-size-sm)',
                        lineHeight: 'var(--line-height-normal)',
                        margin: 0
                      }}>
                        Select multiple variants to test different approaches. Meta's algorithm will automatically optimize 
                        to show the best-performing ads to your target audience. You can also upload new assets to generate 
                        more variations on the fly.
                      </p>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}

            {/* Step 6: Review & Launch */}
            {currentStep === 6 && (
              <div className="form-section">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--spacing-6)'
                }}>
                  <h3 className="form-section-title">
                  {isExistingCampaignMode ? 'AdSet & Ads Review' : 'Campaign Review & Launch'}
                </h3>
                {isExistingCampaignMode && (
                  <div style={{
                    padding: 'var(--spacing-3)',
                    background: 'var(--color-primaryTranslucent)',
                    borderRadius: 'var(--radius-card)',
                    border: '1px solid var(--color-primaryBorder)',
                    marginBottom: 'var(--spacing-3)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-label)'
                  }}>
                    <strong>📂 Adding to existing campaign:</strong><br />
                    {existingMetaCampaigns.find(c => c.id === selectedExistingCampaignId)?.name || selectedExistingCampaignId}
                    <span style={{ marginLeft: 'var(--spacing-1)', color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-xs)' }}>
                      (ID: {selectedExistingCampaignId})
                    </span>
                  </div>
                )}
                  
                  {/* Mode Toggle */}
                  <div style={{
                    display: 'flex',
                    background: 'var(--color-systemFill)',
                    borderRadius: '12px',
                    padding: '4px',
                    border: '1px solid var(--color-border)'
                  }}>
                    <button
                      onClick={() => setIsMultiCampaignMode(false)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: !isMultiCampaignMode ? 'var(--color-card)' : 'transparent',
                        color: !isMultiCampaignMode ? 'var(--color-primary)' : 'var(--color-secondaryLabel)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: !isMultiCampaignMode ? '0 1px 3px var(--color-shadowLight)' : 'none'
                      }}
                    >
                      Single Campaign
                    </button>
                    <button
                      onClick={() => setIsMultiCampaignMode(true)}
                      disabled={selectedVariants.length < 2}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: isMultiCampaignMode ? 'var(--color-card)' : 'transparent',
                        color: selectedVariants.length < 2 ? 'var(--color-tertiaryLabel)' : 
                               isMultiCampaignMode ? 'var(--color-primary)' : 'var(--color-secondaryLabel)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        cursor: selectedVariants.length < 2 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: isMultiCampaignMode ? '0 1px 3px var(--color-shadowLight)' : 'none'
                      }}
                    >
                      Multiple Campaigns
                    </button>
                  </div>
                </div>

                {/* Landing Page URL Configuration */}
                <div style={{ 
                  background: 'var(--color-systemFill)',
                  borderRadius: '12px',
                  padding: 'var(--spacing-6)',
                  marginBottom: 'var(--spacing-6)'
                }}>
                  <h4 style={{ 
                    fontSize: 'var(--font-size-lg)', 
                    fontWeight: 'var(--font-weight-semibold)',
                    marginBottom: 'var(--spacing-4)',
                    color: 'var(--color-label)'
                  }}>
                    🔗 Landing Page Configuration
                  </h4>
                  
                  <div className="form-group">
                    <label className="form-label">Ad Destination</label>
                    <select
                      value={campaign.messengerDestination || 'website'}
                      onChange={(e) => setCampaign(prev => ({ 
                        ...prev, 
                        messengerDestination: e.target.value as 'website' | 'page',
                        // Clear landing page URL when switching to Messenger
                        ...(e.target.value === 'page' && { landingPageUrl: '' })
                      }))}
                      className="form-select"
                    >
                      <option value="website">🌐 Website Landing Page</option>
                      <option value="page">💬 Messenger Conversation</option>
                    </select>
                    <small className="form-help">
                      Choose where users will be directed when they click your ad
                    </small>
                  </div>
                  
                  {/* Only show Landing Page URL for website destination */}
                  {(campaign.messengerDestination === 'website' || !campaign.messengerDestination) && (
                    <div className="form-group">
                      <label className="form-label">Landing Page URL</label>
                      <input
                        type="url"
                        placeholder={`Default: ${selectedPreset?.website || 'https://martimart.com'}`}
                        value={campaign.landingPageUrl || ''}
                        onChange={(e) => setCampaign(prev => ({ ...prev, landingPageUrl: e.target.value }))}
                        className="form-input"
                      />
                      <small className="form-help">
                        Optional: Override the default landing page URL. Leave empty to use the client's website ({selectedPreset?.website || 'https://martimart.com'}).
                      </small>
                    </div>
                  )}
                  
                  {/* Show Messenger configuration for Messenger destination */}
                  {campaign.messengerDestination === 'page' && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                      border: '2px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: 'var(--border-radius-lg)',
                      padding: 'var(--spacing-4)',
                      marginTop: 'var(--spacing-3)'
                    }}>
                      <h5 style={{ 
                        color: 'var(--color-primary)', 
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        marginBottom: 'var(--spacing-3)'
                      }}>
                        💬 Messenger Configuration
                      </h5>
                      
                      <div className="form-group">
                        <label className="form-label">Call-to-Action Button</label>
                        <select
                          value={campaign.messengerCallToAction || 'Send Message'}
                          onChange={(e) => setCampaign(prev => ({ 
                            ...prev, 
                            messengerCallToAction: e.target.value
                          }))}
                          className="form-select"
                        >
                          <option value="Send Message">Send Message</option>
                          <option value="Contact Us">Contact Us</option>
                          <option value="Get Quote">Get Quote</option>
                          <option value="Learn More">Learn More</option>
                          <option value="Book Now">Book Now</option>
                          <option value="Get Support">Get Support</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                          <label className="form-label">Welcome Message (Optional)</label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                            <input
                              type="checkbox"
                              checked={!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Use default - clear custom greeting
                                  setCampaign(prev => ({ ...prev, messengerGreeting: '' }));
                                } else {
                                  // Use custom - set to current default or empty
                                  setCampaign(prev => ({ 
                                    ...prev, 
                                    messengerGreeting: selectedPreset?.defaultMessengerGreeting || ''
                                  }));
                                }
                              }}
                            />
                            <span>Use Default</span>
                          </label>
                        </div>
                        <textarea
                          value={campaign.messengerGreeting || selectedPreset?.defaultMessengerGreeting || ''}
                          onChange={(e) => setCampaign(prev => ({ 
                            ...prev, 
                            messengerGreeting: e.target.value
                          }))}
                          className="form-textarea"
                          placeholder={selectedPreset?.defaultMessengerGreeting || "Hi {{user_first_name}}! Thanks for clicking our ad. How can we help you today?"}
                          rows={2}
                          disabled={!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting}
                          style={{
                            opacity: (!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting) ? 0.7 : 1,
                            cursor: (!campaign.messengerGreeting || campaign.messengerGreeting === selectedPreset?.defaultMessengerGreeting) ? 'not-allowed' : 'text'
                          }}
                        />
                        <small className="form-help">
                          This message will be sent when users start a conversation from your ad
                        </small>
                        <div style={{
                          marginTop: 'var(--spacing-2)',
                          padding: 'var(--spacing-2)',
                          background: 'var(--color-systemGray6)',
                          borderRadius: 'var(--border-radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-secondaryLabel)'
                        }}>
                          <strong>Available Macros:</strong><br/>
                          • <code>{`{{user_first_name}}`}</code> - User's first name (Facebook)<br/>
                          • <code>{`{{ad_name}}`}</code> - Your ad name (Custom)<br/>
                          • <code>{`{{ad_headline}}`}</code> - Your ad headline (Custom)<br/>
                          • <code>{`{{ad_body}}`}</code> - Your ad body text (Custom)
                        </div>
                        
                        {/* Quick Replies Configuration */}
                        <div style={{ marginTop: 'var(--spacing-4)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                            <label className="form-label">Quick Reply Buttons (Optional)</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                              <input
                                type="checkbox"
                                checked={!campaign.messengerQuickReplies || JSON.stringify(campaign.messengerQuickReplies) === JSON.stringify(selectedPreset?.defaultMessengerQuickReplies)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Use default - clear custom quick replies
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: undefined }));
                                  } else {
                                    // Use custom - set to current default or empty array
                                    setCampaign(prev => ({ 
                                      ...prev, 
                                      messengerQuickReplies: selectedPreset?.defaultMessengerQuickReplies || []
                                    }));
                                  }
                                }}
                              />
                              <span>Use Default</span>
                            </label>
                          </div>
                          <div style={{ 
                            border: '1px solid var(--color-border)', 
                            borderRadius: 'var(--border-radius-md)', 
                            padding: 'var(--spacing-3)',
                            background: 'var(--color-card)',
                            opacity: (!campaign.messengerQuickReplies || JSON.stringify(campaign.messengerQuickReplies) === JSON.stringify(selectedPreset?.defaultMessengerQuickReplies)) ? 0.7 : 1
                          }}>
                            {(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || []).map((reply, index) => (
                              <div key={index} style={{ 
                                display: 'flex', 
                                gap: 'var(--spacing-2)', 
                                marginBottom: 'var(--spacing-2)',
                                alignItems: 'center'
                              }}>
                                <input
                                  type="text"
                                  placeholder="Button text (max 20 chars)"
                                  value={reply.title}
                                  maxLength={20}
                                  onChange={(e) => {
                                    const newReplies = [...(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [])];
                                    newReplies[index] = { ...reply, title: e.target.value };
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: 'var(--spacing-2)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--border-radius-sm)',
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                />
                                <input
                                  type="text"
                                  placeholder="Payload (internal identifier)"
                                  value={reply.payload}
                                  onChange={(e) => {
                                    const newReplies = [...(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [])];
                                    newReplies[index] = { ...reply, payload: e.target.value };
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: 'var(--spacing-2)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--border-radius-sm)',
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newReplies = [...(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [])];
                                    newReplies.splice(index, 1);
                                    setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                  }}
                                  style={{
                                    padding: 'var(--spacing-1)',
                                    background: 'var(--color-error)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--border-radius-sm)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)'
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            
                            {(campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || []).length < 13 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const currentReplies = campaign.messengerQuickReplies || selectedPreset?.defaultMessengerQuickReplies || [];
                                  const newReplies = [...currentReplies, { title: '', payload: '' }];
                                  setCampaign(prev => ({ ...prev, messengerQuickReplies: newReplies }));
                                }}
                                style={{
                                  padding: 'var(--spacing-2)',
                                  background: 'var(--color-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 'var(--border-radius-sm)',
                                  cursor: 'pointer',
                                  fontSize: 'var(--font-size-sm)',
                                  width: '100%'
                                }}
                              >
                                + Add Quick Reply Button
                              </button>
                            )}
                            
                            <small style={{ 
                              display: 'block', 
                              marginTop: 'var(--spacing-2)', 
                              color: 'var(--color-secondaryLabel)',
                              fontSize: 'var(--font-size-xs)'
                            }}>
                              Quick reply buttons help users respond quickly. Max 13 buttons, 20 characters each.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Campaign Summary */}
                <div style={{ 
                  background: 'var(--color-systemFill)',
                  borderRadius: '12px',
                  padding: 'var(--spacing-6)',
                  marginBottom: 'var(--spacing-6)'
                }}>
                  <h4 style={{ 
                    fontSize: 'var(--font-size-lg)', 
                    fontWeight: 'var(--font-weight-semibold)',
                    marginBottom: 'var(--spacing-4)',
                    color: 'var(--color-label)'
                  }}>
                    📋 Campaign Summary
                  </h4>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: 'var(--spacing-4)' 
                  }}>
                    <div>
                      <strong>Campaign:</strong> <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-tertiaryLabel)' }}>(editable)</span><br />
                      {isMultiCampaignMode ? (
                        <div>
                          <input
                            type="text"
                            value={multiCampaignConfig.baseName || ''}
                            onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, baseName: e.target.value }))}
                            placeholder="Enter campaign base name"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-label)',
                              marginBottom: '4px'
                            }}
                          />
                        </div>
                      ) : (
                        <div>
                          <input
                            type="text"
                            value={campaign.name || ''}
                            onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter campaign name"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-label)',
                              marginBottom: '4px'
                            }}
                          />
                        </div>
                      )}
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondaryLabel)' }}>
                        {campaignTypes.find(t => t.id === campaign.campaignType)?.name}
                      </span>
                    </div>
                    <div>
                      <strong>Budget:</strong><br />
                      {isMultiCampaignMode 
                        ? `$${multiCampaignConfig.budgetPerCampaign || selectedPreset?.defaultBudget || 0} per campaign (${multiCampaignConfig.numCampaigns} campaigns = $${(multiCampaignConfig.budgetPerCampaign || selectedPreset?.defaultBudget || 0) * multiCampaignConfig.numCampaigns} total)`
                        : `$${campaign.budget || selectedPreset?.defaultBudget || 0} (${campaign.budgetType})`
                      }<br />
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondaryLabel)' }}>
                        {isMultiCampaignMode 
                          ? (multiCampaignConfig.duration === null ? 'Unlimited' : `${multiCampaignConfig.duration || selectedPreset?.defaultDuration || 7} days`)
                          : (campaign.duration === null ? 'Unlimited' : `${campaign.duration || selectedPreset?.defaultDuration || 7} days`)
                        }
                      </span>
                    </div>
                    <div>
                      <strong>Targeting:</strong><br />
                      {campaign.targeting?.countries?.join(', ') || selectedPreset?.defaultCountries?.join(', ') || 'No countries selected'}<br />
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondaryLabel)' }}>
                        Ages {campaign.targeting?.ageMin || selectedPreset?.defaultAgeMin || 18}-{campaign.targeting?.ageMax || selectedPreset?.defaultAgeMax || 65}
                      </span>
                    </div>
                    <div>
                      <strong>Ad Variants:</strong><br />
                      {selectedVariants.length} variants selected<br />
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondaryLabel)' }}>
                        {isMultiCampaignMode 
                          ? `Distributed across ${multiCampaignConfig.numCampaigns} campaigns (${multiCampaignConfig.distributionMethod})`
                          : 'All variants in one campaign'
                        }
                      </span>
                    </div>
                  </div>

                  {/* Selected Variants Preview */}
                  {selectedVariants.length > 0 && (
                    <div style={{
                      marginTop: 'var(--spacing-6)',
                      padding: 'var(--spacing-4)',
                      background: 'var(--color-systemFill)',
                      borderRadius: '12px'
                    }}>
                      <h5 style={{
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-label)',
                        marginBottom: 'var(--spacing-3)'
                      }}>
                        📝 Selected Ad Variants ({selectedVariants.length})
                      </h5>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: 'var(--spacing-3)',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {variants
                          .filter(v => selectedVariants.includes(v.id))
                          .map(variant => (
                            <div
                              key={variant.id}
                              style={{
                                padding: 'var(--spacing-3)',
                                background: 'var(--color-card)',
                                borderRadius: '8px',
                                border: '1px solid var(--color-border)'
                              }}
                            >
                              <h6 style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-semibold)',
                                color: 'var(--color-label)',
                                marginBottom: 'var(--spacing-1)',
                                lineHeight: 'var(--line-height-tight)'
                              }}>
                                {variant.headline}
                              </h6>
                              <p style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-secondaryLabel)',
                                margin: 0,
                                lineHeight: 'var(--line-height-snug)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {variant.body}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Single Campaign Mode */}
                {!isMultiCampaignMode && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-primaryTranslucent)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-primaryBorder)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-label)',
                      textAlign: 'center',
                      maxWidth: '600px'
                    }}>
                      🚀 <strong>Single Campaign:</strong> All selected variants will be created as ads within one campaign for A/B testing.
                    </div>
                    
                    <button
                      onClick={isExistingCampaignMode ? handleAddToExistingCampaign : handleCreateCampaign}
                      disabled={isCreating || selectedVariants.length === 0}
                      style={{
                        background: (isCreating || selectedVariants.length === 0) ? 'var(--color-systemGray4)' : 'var(--color-primary)',
                        color: (isCreating || selectedVariants.length === 0) ? 'var(--color-tertiaryLabel)' : 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: 'var(--spacing-4) var(--spacing-8)',
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 600,
                        cursor: (isCreating || selectedVariants.length === 0) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--spacing-2)',
                        minWidth: '200px'
                      }}
                    >
                      {isCreating ? '🚀 Creating...' :
                        isExistingCampaignMode
                          ? '📂 Add AdSet & Ads to Campaign'
                          : campaign.campaignType === 'click_to_messenger'
                          ? '💬 Launch Messenger Campaign'
                          : campaign.messengerDestination === 'page'
                          ? '💬 Launch Campaign to Messenger'
                          : '🚀 Launch Campaign'}
                    </button>
                  </div>
                )}

                {/* Multiple Campaigns Mode */}
                {isMultiCampaignMode && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-6)'
                  }}>
                    <div style={{
                      padding: 'var(--spacing-3)',
                      background: 'var(--color-warningSubtle)',
                      borderRadius: '12px',
                      border: '1px solid var(--color-warningBorder)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-label)',
                      textAlign: 'center'
                    }}>
                      🎯 <strong>Multiple Campaigns:</strong> Create several campaigns with different ad distributions for better testing and organization.
                    </div>

                    {/* Multi-Campaign Configuration */}
                    <div style={{
                      background: 'var(--color-systemFill)',
                      borderRadius: '12px',
                      padding: 'var(--spacing-6)',
                      border: '1px solid var(--color-border)'
                    }}>
                      <h4 style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-label)',
                        marginBottom: 'var(--spacing-4)'
                      }}>
                        ⚙️ Multiple Campaigns Configuration
                      </h4>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: 'var(--spacing-4)'
                      }}>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-label)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            Base Campaign Name
                          </label>
                          <input
                            type="text"
                            value={multiCampaignConfig.baseName}
                            onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, baseName: e.target.value }))}
                            placeholder="e.g., Summer Sale 2024"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-base)',
                              color: 'var(--color-label)'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-label)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            Number of Campaigns
                          </label>
                          <input
                            type="number"
                            min="2"
                            max="10"
                            value={multiCampaignConfig.numCampaigns}
                            onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, numCampaigns: parseInt(e.target.value) || 2 }))}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-base)',
                              color: 'var(--color-label)'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-label)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            Budget per Campaign ($)
                          </label>
                          <input
                            type="number"
                            min="10"
                            value={multiCampaignConfig.budgetPerCampaign || selectedPreset?.defaultBudget || 0}
                            onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, budgetPerCampaign: parseInt(e.target.value) || (selectedPreset?.defaultBudget || 100) }))}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-base)',
                              color: 'var(--color-label)'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-label)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            Distribution Method
                          </label>
                          <select
                            value={multiCampaignConfig.distributionMethod}
                            onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, distributionMethod: e.target.value as 'round_robin' | 'sequential' }))}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-base)',
                              color: 'var(--color-label)'
                            }}
                          >
                            <option value="round_robin">Round Robin (Even Distribution)</option>
                            <option value="sequential">Sequential (Grouped)</option>
                          </select>
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-label)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={multiCampaignConfig.startDate}
                            onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, startDate: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid var(--color-border)',
                              background: 'var(--color-card)',
                              fontSize: 'var(--font-size-base)',
                              color: 'var(--color-label)'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-label)',
                            marginBottom: 'var(--spacing-2)'
                          }}>
                            Campaign Duration
                          </label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
                            <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="multiDurationType"
                                  checked={multiCampaignConfig.duration !== null && multiCampaignConfig.duration !== undefined}
                                  onChange={() => setMultiCampaignConfig(prev => ({ ...prev, duration: selectedPreset?.defaultDuration || 7 }))}
                                />
                                <span>Limited Duration</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="multiDurationType"
                                  checked={multiCampaignConfig.duration === null || multiCampaignConfig.duration === undefined}
                                  onChange={() => setMultiCampaignConfig(prev => ({ ...prev, duration: null }))}
                                />
                                <span>Unlimited/Ongoing</span>
                              </label>
                            </div>
                            {(multiCampaignConfig.duration !== null && multiCampaignConfig.duration !== undefined) && (
                              <input
                                type="number"
                                min="1"
                                max="365"
                                value={multiCampaignConfig.duration || selectedPreset?.defaultDuration || 7}
                                onChange={(e) => setMultiCampaignConfig(prev => ({ ...prev, duration: parseInt(e.target.value) || (selectedPreset?.defaultDuration || 7) }))}
                                placeholder="Days"
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--color-border)',
                                  background: 'var(--color-card)',
                                  fontSize: 'var(--font-size-base)',
                                  color: 'var(--color-label)'
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Distribution Preview */}
                      {selectedVariants.length > 0 && multiCampaignConfig.numCampaigns > 1 && (
                        <div style={{
                          marginTop: 'var(--spacing-6)',
                          padding: 'var(--spacing-4)',
                          background: 'var(--color-primaryTranslucent)',
                          borderRadius: '8px',
                          border: '1px solid var(--color-primaryBorder)'
                        }}>
                          <h5 style={{
                            fontSize: 'var(--font-size-base)',
                            fontWeight: 600,
                            color: 'var(--color-primary)',
                            marginBottom: 'var(--spacing-3)'
                          }}>
                            📊 Distribution Preview
                          </h5>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: 'var(--spacing-3)'
                          }}>
                            {Array.from({ length: multiCampaignConfig.numCampaigns }, (_, i) => {
                              const variantsPerCampaign = Math.ceil(selectedVariants.length / multiCampaignConfig.numCampaigns);
                              const startIdx = multiCampaignConfig.distributionMethod === 'sequential' 
                                ? i * variantsPerCampaign 
                                : i;
                              const campaignVariants = multiCampaignConfig.distributionMethod === 'sequential'
                                ? selectedVariants.slice(startIdx, startIdx + variantsPerCampaign)
                                : selectedVariants.filter((_, idx) => idx % multiCampaignConfig.numCampaigns === i);
                              
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: 'var(--spacing-3)',
                                    background: 'var(--color-card)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--color-border)'
                                  }}
                                >
                                  <h6 style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    color: 'var(--color-label)',
                                    marginBottom: 'var(--spacing-2)'
                                  }}>
                                    {multiCampaignConfig.baseName || 'Campaign'} #{i + 1}
                                  </h6>
                                  <p style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-secondaryLabel)',
                                    margin: 0
                                  }}>
                                    {campaignVariants.length} variants • ${multiCampaignConfig.budgetPerCampaign || selectedPreset?.defaultBudget || 0} budget
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Launch Multiple Campaigns */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--spacing-4)',
                      alignItems: 'center'
                    }}>
                      <button
                        onClick={handleCreateMultipleCampaigns}
                        disabled={isCreating || selectedVariants.length < 2 || !multiCampaignConfig.baseName.trim()}
                        style={{
                          background: (isCreating || selectedVariants.length < 2 || !multiCampaignConfig.baseName.trim()) 
                            ? 'var(--color-systemGray4)' 
                            : 'var(--color-warning)',
                          color: (isCreating || selectedVariants.length < 2 || !multiCampaignConfig.baseName.trim()) 
                            ? 'var(--color-tertiaryLabel)' 
                            : 'white',
                          border: 'none',
                          borderRadius: '12px',
                          padding: 'var(--spacing-4) var(--spacing-8)',
                          fontSize: 'var(--font-size-lg)',
                          fontWeight: 600,
                          cursor: (isCreating || selectedVariants.length < 2 || !multiCampaignConfig.baseName.trim()) 
                            ? 'not-allowed' 
                            : 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 'var(--spacing-2)',
                          minWidth: '250px'
                        }}
                      >
                        {isCreating ? '🚀 Creating...' : `🚀 Launch ${multiCampaignConfig.numCampaigns} Campaigns`}
                      </button>
                      
                      {(selectedVariants.length < 2 || !multiCampaignConfig.baseName.trim()) && (
                        <p style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-tertiaryLabel)',
                          margin: '0',
                          textAlign: 'center'
                        }}>
                          {selectedVariants.length < 2 
                            ? 'Select at least 2 variants for multiple campaigns'
                            : 'Enter a base campaign name'
                          }
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <p style={{ 
                  marginTop: 'var(--spacing-4)', 
                  fontSize: 'var(--font-size-sm)', 
                  color: 'var(--color-secondaryLabel)',
                  textAlign: 'center'
                }}>
                  Campaigns will be created in PAUSED status for your review
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="step-navigation">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="btn btn-secondary"
              >
                ← Previous
              </button>
              
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondaryLabel)' }}>
                Step {currentStep} of {steps.length}
              </div>
              
              <button
                onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                disabled={currentStep === steps.length || !canProceed()}
                className="btn btn-primary"
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdvancedCampaignPage; 